import React from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import {
  FirstPage, LastPage, NavigateBefore, NavigateNext,
} from '@mui/icons-material';
import { colors, typography } from '../../theme/designSystem';

/**
 * Paginação — o único componente do sistema (decisão do Hilton, 02/09/2026).
 *
 * Havia dois, com ordem e texto diferentes: um dizia "Mostrando 1-10 de 468 pacientes" à
 * esquerda, o outro "Itens por página" à esquerda e "Exibindo 1-1 de 1" no meio. O usuário
 * relia a barra a cada tela.
 *
 * Formato fixo: **"Mostrando X-Y de Z itens"** à esquerda; "Itens por página" e a navegação à
 * direita. Fundo **transparente** — a barra vive dentro do painel branco da página e não
 * precisa de caixa própria.
 *
 * "itens" acompanha o padrão da contagem do resultado (ver `ResultCount`).
 */
interface StandardPaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

/** 50 por pagina e o padrao do sistema (decisao do Hilton, 02/09/2026). */
export const ITENS_POR_PAGINA_PADRAO = 50;

const OPCOES_PADRAO = [10, 25, 50, 100];

/** Páginas visíveis: as vizinhas da atual, com a primeira e a última sempre à vista. */
const paginasVisiveis = (atual: number, total: number): (number | '...')[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const paginas: (number | '...')[] = [1];
  const ini = Math.max(2, atual - 1);
  const fim = Math.min(total - 1, atual + 1);

  if (ini > 2) paginas.push('...');
  for (let p = ini; p <= fim; p += 1) paginas.push(p);
  if (fim < total - 1) paginas.push('...');
  paginas.push(total);

  return paginas;
};

const botaoNav = {
  minWidth: 32,
  height: 32,
  border: 'none',
  background: 'transparent',
  color: colors.textSecondary,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  '&:disabled': { color: colors.borderSubtle, cursor: 'default' },
  '&:hover:not(:disabled)': { backgroundColor: colors.backgroundAlt },
};

const StandardPagination: React.FC<StandardPaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = OPCOES_PADRAO,
}) => {
  const primeiro = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const ultimo = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        // Transparente: a barra vive dentro do painel branco e não precisa de caixa própria
        backgroundColor: 'transparent',
        paddingTop: '1rem',
        marginTop: '0.5rem',
        borderTop: `1px solid ${colors.backgroundAlt}`,
      }}
    >
      <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
        Mostrando {primeiro}-{ultimo} de <strong>{totalItems}</strong>{' '}
        {totalItems === 1 ? 'item' : 'itens'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
            Itens por página:
          </Typography>
          <Select
            size="small"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            sx={{
              height: 32,
              minWidth: 78,
              fontSize: typography.fontSize.sm,
              backgroundColor: colors.white,
              // Com altura reduzida o MUI não reserva espaço para a seta e ela cobre o número
              '& .MuiSelect-select': { paddingRight: '32px !important', paddingLeft: '10px' },
            }}
          >
            {itemsPerPageOptions.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Box component="button" sx={botaoNav} onClick={() => onPageChange(1)} disabled={currentPage === 1} aria-label="Primeira página">
            <FirstPage fontSize="small" />
          </Box>
          <Box component="button" sx={botaoNav} onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="Página anterior">
            <NavigateBefore fontSize="small" />
          </Box>

          {paginasVisiveis(currentPage, totalPages).map((p, i) => (
            p === '...' ? (
              <Typography key={`sep-${i}`} sx={{ px: 0.5, color: colors.textSecondary, fontSize: typography.fontSize.sm }}>
                …
              </Typography>
            ) : (
              <Box
                key={p}
                component="button"
                onClick={() => onPageChange(p)}
                sx={{
                  ...botaoNav,
                  borderRadius: '50%',
                  fontSize: typography.fontSize.sm,
                  fontWeight: p === currentPage ? typography.fontWeight.semibold : 'normal',
                  backgroundColor: p === currentPage ? colors.primary : 'transparent',
                  color: p === currentPage ? colors.white : colors.textSecondary,
                  '&:hover': {
                    backgroundColor: p === currentPage ? colors.primary : colors.backgroundAlt,
                  },
                }}
              >
                {p}
              </Box>
            )
          ))}

          <Box component="button" sx={botaoNav} onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Próxima página">
            <NavigateNext fontSize="small" />
          </Box>
          <Box component="button" sx={botaoNav} onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} aria-label="Última página">
            <LastPage fontSize="small" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StandardPagination;
